import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import {
  P360_FIRST_FYEAR,
  PLAN_REQUEST_FOR_PREV_YEARS,
} from 'legacy/lut/components/widgets/public/partner360/planning/config'
import { selectJBPSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { IKIBusinessPriority } from 'legacy/lut/features/partner360/jbpSettings/models/kiBusinessPriority'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useGetKeyInitiativesQuery } from 'partner360/entities/keyInitiatives'
import { useGetPlanningQuery } from 'partner360/entities/planning'
import { useFinancialPlanQuality } from 'partner360/features/financialPlanQuality'
import {
  buildKeyInitiativesFinancialData,
  filterBusinessPriorities,
  getInitiativesPlanQuality,
} from 'partner360/features/keyInitiatives'

import { getPlanningMeasureQuality } from '../lib/getPlanningMeasureQuality'

import type { TPlanReviewOffering } from './types'
import { usePlanReviewPartnerSettings } from './usePlanReviewPartnerSettings'

export const PLAN_REVIEW_QUALITY_CURRENCY = 'USD'

interface IUsePlanReviewQualityParams {
  partnerId: string
  fYear: number
  programType: TPlanReviewOffering
  isGlobal: boolean
  refreshPlanningOnOpen?: boolean
}

export const usePlanReviewQuality = ({
  partnerId,
  fYear,
  programType,
  isGlobal,
  refreshPlanningOnOpen = false,
}: IUsePlanReviewQualityParams) => {
  const { settings: partnerSettings, isLoading: isPartnerSettingsLoading } =
    usePlanReviewPartnerSettings({ partnerId, fYear, programType })
  const { geoMarket: partnerMarket, isRegionalDistributor, actualsType } = partnerSettings
  const { kiBusinessPriorities, keyObjectives } = useSelector(selectJBPSettings)
  const financial = useFinancialPlanQuality({
    partnerId,
    fYear,
    programType,
    isGlobal,
    partnerSettings: {
      partnerFlags: partnerSettings.partnerFlags,
      isRegionalDistributor,
      sectionSettings: partnerSettings.sectionSettings,
    },
    skip: isPartnerSettingsLoading,
  })
  const { data: initiatives = [], isFetching: isInitiativesFetching } = useGetKeyInitiativesQuery({
    partnerId,
    fYear,
    programType,
  })

  /* `isRegionalDistributor` is part of the request, so it waits for the partner's own value. */
  const {
    data: planningData,
    isFetching: isInitiativeFinancialsFetching,
    isError: isPlanningError,
  } = useGetPlanningQuery(
    {
      partnerId,
      fYear,
      programType,
      fromFYear: Math.max(fYear - PLAN_REQUEST_FOR_PREV_YEARS, P360_FIRST_FYEAR),
      isRegionalDistributor,
    },
    // CategoryPlanning saves through its SP, without invalidating this query's cached rows.
    // An acknowledgment review must therefore read the saved AVT again when it opens.
    { skip: isPartnerSettingsLoading, refetchOnMountOrArgChange: refreshPlanningOnOpen }
  )
  const initiativeFinancials = useMemo(
    () => buildKeyInitiativesFinancialData(planningData?.Planning ?? [], fYear, false),
    [planningData?.Planning, fYear]
  )

  const planningMeasureQuality = useMemo(
    () =>
      getPlanningMeasureQuality({
        rows: planningData?.Planning ?? [],
        partnerId,
        fYear,
        programType,
        isDistributor: partnerSettings.isDistributor,
        actualsTypeDefault: partnerSettings.actualsType,
        sectionSettings: partnerSettings.sectionSettings,
      }),
    [planningData?.Planning, partnerId, fYear, programType, partnerSettings]
  )

  const normalizedInitiatives = useMemo(
    () => initiatives.filter((initiative) => initiative.Parent),
    [initiatives]
  )
  const businessPriorities = useMemo(() => {
    if (programType !== null) {
      return filterBusinessPriorities({
        priorities: kiBusinessPriorities,
        fYear,
        programType,
        market: partnerMarket,
      })
    }

    /* Across every offering: the priorities of each offering the plan holds rows for, once each. */
    const planOfferings = new Set(
      (planningData?.Planning ?? []).map((row) => row.ProgramTypes?.trim()).filter(Boolean)
    )
    const prioritiesByUid = new Map<string, IKIBusinessPriority>()

    planOfferings.forEach((offering) =>
      filterBusinessPriorities({
        priorities: kiBusinessPriorities,
        fYear,
        programType: offering as ProgramTypes,
        market: partnerMarket,
      }).forEach((priority) => prioritiesByUid.set(priority.uid.trim().toLowerCase(), priority))
    )

    return [...prioritiesByUid.values()]
  }, [fYear, kiBusinessPriorities, partnerMarket, planningData?.Planning, programType])
  const initiativesQuality = useMemo(
    () =>
      getInitiativesPlanQuality({
        keyObjectives,
        data: normalizedInitiatives,
        finData: initiativeFinancials,
        businessPriorities,
        activeCurrency: PLAN_REVIEW_QUALITY_CURRENCY,
        actualsType,
      }),
    [actualsType, businessPriorities, initiativeFinancials, normalizedInitiatives, keyObjectives]
  )

  return {
    financialQuality: financial.quality,
    hasFinancialPlan: financial.hasPlan,
    initiativesQuality,
    planningMeasureQuality,
    isPlanningMeasureError:
      (!isPartnerSettingsLoading && !partnerSettings.isConfigurationAvailable) || isPlanningError,
    isLoading:
      isPartnerSettingsLoading ||
      financial.isFetching ||
      isInitiativesFetching ||
      isInitiativeFinancialsFetching,
  }
}

export type TInitiativesPlanQuality = ReturnType<typeof getInitiativesPlanQuality>
